import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { produce } from 'immer';

import {
  type PaginationRequest,
  safePageNumber,
  safePageSize,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '#packages/data-collection/utils/pagination';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';

export const PAGE_SIZE_PARAM = 'page_size';
export const PAGE_NUMBER_PARAM = 'page_number';

export type PaginationControl = {
  /**
   * Current page number (1-based)
   */
  currentPageNumber: number;

  /**
   * Current page size (number of items per page)
   */
  currentPageSize: number;

  /**
   * Go to the next page
   */
  nextPage: () => void;

  /**
   * Go to the previous page
   */
  previousPage: () => void;

  /**
   * Change to a specific page number
   */
  changePageNumber: (newPageNumber: number) => void;

  /**
   * Change to a specific page size
   */
  changePageSize: (newPageSize: number) => void;
};

/**
 * A hook to manage pagination state using URL search parameters.
 * (This is the base hook that does not depend on any routing library)
 */
export const usePagination = (
  defaults: PaginationRequest = {
    pageNumber: DEFAULT_PAGE_NUMBER,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  { logger }: LoggingContext = {},
): PaginationControl => {
  const [pagination, setPagination] = useState<PaginationRequest>({
    pageNumber: safePageNumber(defaults.pageNumber),
    pageSize: safePageSize(defaults.pageSize),
  });

  const changePageNumber = useCallback((newPageNumber: number) => {
    setPagination(
      produce((draft) => {
        draft.pageNumber = safePageNumber(newPageNumber);
      }),
    );
  }, []);

  const changePageSize = useCallback((newPageSize: number) => {
    setPagination(
      produce((draft) => {
        draft.pageSize = safePageSize(newPageSize);
      }),
    );
  }, []);

  const nextPage = useCallback(() => {
    setPagination(
      produce((draft) => {
        draft.pageNumber = safePageNumber(draft.pageNumber + 1);
      }),
    );
  }, []);

  const previousPage = useCallback(() => {
    setPagination(
      produce((draft) => {
        // ensure we do not go below 1
        draft.pageNumber = safePageNumber(draft.pageNumber - 1);
      }),
    );
  }, []);

  logger?.debug(
    {
      currentPageNumber: pagination.pageNumber,
      currentPageSize: pagination.pageSize,
    },
    'usePagination current state',
  );

  return {
    currentPageNumber: pagination.pageNumber,
    currentPageSize: pagination.pageSize,

    changePageNumber,
    changePageSize,

    nextPage,
    previousPage,
  };
};

export const usePaginationSearchParams = (
  defaults: PaginationRequest = {
    pageNumber: DEFAULT_PAGE_NUMBER,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  { logger }: LoggingContext = {},
): PaginationControl => {
  // Use URL search params as the source of truth
  const [searchParams, setSearchParams] = useSearchParams({
    [PAGE_NUMBER_PARAM]: defaults.pageNumber.toString(),
    [PAGE_SIZE_PARAM]: defaults.pageSize.toString(),
  });

  // Initialize pagination state from search params
  const pagination = usePagination({
    pageNumber: safePageNumber(searchParams.get(PAGE_NUMBER_PARAM)),
    pageSize: safePageSize(searchParams.get(PAGE_SIZE_PARAM)),
  });

  // Sync pagination state to URL search params
  useEffect(() => {
    logger?.debug(
      {
        currentPageNumber: pagination.currentPageNumber,
        currentPageSize: pagination.currentPageSize,
      },
      'Syncing pagination state to URL search params',
    );

    setSearchParams((prev) => {
      prev.set(PAGE_NUMBER_PARAM, pagination.currentPageNumber.toString());
      prev.set(PAGE_SIZE_PARAM, pagination.currentPageSize.toString());

      return prev;
    });
  }, [pagination.currentPageNumber, pagination.currentPageSize]);

  return pagination;
};
